import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notify } from '../helper/notify';
import { useDispatch } from 'react-redux';
import { Freelancer, Customer, CustomerResponse, CustomerParams } from '../api/customer';
import { fetchFreelancers, FreelancerResponse } from '../api/freelancer';
import { getAllFreelancers } from '../../redux/auth/authSlice';
import apiCall from '../../api';

interface ReactivateState {
  show: boolean;
  id:number;
  name: string;
}

interface UseFreelancersResult {
  freelancers: Freelancer[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  paginatedFreelancers: Freelancer[];
  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  refetch: () => Promise<void>;
  // Reactivate freelancer functionality
  isReactivate: ReactivateState;
  setIsReactivate: (state: ReactivateState | ((prev: ReactivateState) => ReactivateState)) => void;
  handleReactivateFreelancer: (id: number , message: string) => Promise<void>;
  reactivateLoading: boolean;
}

export const useFreelancers = (pageSize: number = 10): UseFreelancersResult => {
  const dispatch = useDispatch();
  const [freelancers, setFreelancers] = useState<Freelancer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Reactivate freelancer state
  const [reactivateLoading, setReactivateLoading] = useState<boolean>(false);
  const [isReactivate, setIsReactivate] = useState<ReactivateState>({
    show: false,
    id: 0,
    name: "",
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: CustomerParams = {
        ...(searchQuery && { searchQuery }),
        pageNumber: currentPage,
        pageSize,
      };
      const response: FreelancerResponse = await fetchFreelancers(params);
      if (response && response.response) {
        setFreelancers(response.response);
        setTotalCount(response.totalCount || 0);
      }
    } catch (err) {
      console.error('Failed to fetch freelancers:', err);
      setError((err as Error).message || 'Failed to load freelancers');
      setFreelancers([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate pagination based on server response
  const totalPages = Math.ceil(totalCount / pageSize);
  
  // Use customers directly from API (already paginated on server)
  const paginatedFreelancers = freelancers;

  const handleSetSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  const handleSetCurrentPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const refetch = async () => {
    await fetchData();
  };

  // Reactivate freelancer function
  const handleReactivateFreelancer = async (id: number , message: string) => {
    setReactivateLoading(true);
    try {
      const url = `/freelancer/reactivate-freelancer/${id}`;
      const result = await apiCall.post(url);
      if (result?.status === 200) {
        setReactivateLoading(false);
        notify.success(message);
        setIsReactivate({
          show: false,
          id: 0,
          name: "",
        });
        // Refresh data after reactivation
        dispatch(getAllFreelancers() as any);
        await refetch();
      }
    } catch (err: any) {
      setReactivateLoading(false);
      notify.error(err?.response?.data?.message || 'Failed to reactivate freelancer');
    }
  };

  return {
    freelancers,
    loading,
    error,
    searchQuery,
    currentPage,
    totalPages,
    pageSize,
    totalCount,
    paginatedFreelancers,
    setSearchQuery: handleSetSearchQuery,
    setCurrentPage: handleSetCurrentPage,
    goToNextPage,
    goToPreviousPage,
    refetch,
    isReactivate,
    setIsReactivate,
    handleReactivateFreelancer,
    reactivateLoading,
  };
};
